import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  buildRolePermissionSections,
  canEditPermissions,
  FEATURE_KEYS,
  FEATURE_LABELS,
  getFeatureAccessMatrix,
  ROLE_LABELS,
  ROLE_ORDER,
  saveFeatureAccessMatrix,
  resetFeatureAccessMatrix,
} from "../utils/rbac";
import { getStoredUser } from "../utils/impersonation";

const RolePermissionsPage = () => {
  const user = useMemo(() => getStoredUser(), []);
  const isEditable = canEditPermissions(user);
  const [matrix, setMatrix] = useState(() => getFeatureAccessMatrix());
  const [note, setNote] = useState("");

  const sections = useMemo(() => buildRolePermissionSections(matrix), [matrix]);

  const toggleRoleAccess = (feature, role) => {
    if (feature === "impersonation") return;
    if (["owner", "admin", "super_admin", "master"].includes(role)) return;
    setMatrix((prev) => {
      const current = new Set(prev?.[feature] || []);
      if (current.has(role)) current.delete(role);
      else current.add(role);
      return { ...prev, [feature]: Array.from(current) };
    });
  };

  const onSave = () => {
    saveFeatureAccessMatrix(matrix);
    setNote("Permissions updated successfully.");
  };

  const onReset = () => {
    resetFeatureAccessMatrix();
    setMatrix(getFeatureAccessMatrix());
    setNote("Permissions reset to default.");
  };

  return (
    <Page>
      <Hero>
        <h1>Role Permissions</h1>
        <p>
          {isEditable
            ? "Change role permissions using the matrix below and save."
            : "View-only mode. You do not have permission to edit role access."}
        </p>
      </Hero>

      <EditorCard>
        <HeadRow>
          <h3>Permission Matrix</h3>
          <ActionRow>
            <ActionBtn type="button" onClick={onReset} disabled={!isEditable}>
              Reset
            </ActionBtn>
            <ActionBtn type="button" $primary onClick={onSave} disabled={!isEditable}>
              Save Changes
            </ActionBtn>
          </ActionRow>
        </HeadRow>
        {note && <Hint>{note}</Hint>}
        <TableWrap>
          <MatrixTable>
            <thead>
              <tr>
                <th>Feature</th>
                {ROLE_ORDER.map((role) => (
                  <th key={role}>{ROLE_LABELS[role] || role}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_KEYS.map((feature) => (
                <tr key={feature}>
                  <td>{FEATURE_LABELS[feature] || feature}</td>
                  {ROLE_ORDER.map((role) => {
                    const checked = (matrix?.[feature] || []).includes(role);
                    const locked =
                      !isEditable ||
                      feature === "impersonation" ||
                      ["owner", "admin", "super_admin", "master"].includes(role);
                    return (
                      <td key={`${feature}-${role}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={locked}
                          onChange={() => toggleRoleAccess(feature, role)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </MatrixTable>
        </TableWrap>
      </EditorCard>

      <Grid>
        {sections.map((section) => (
          <Card key={section.title}>
            <Head>
              <Badge style={{ background: `${section.color}20`, color: section.color }}>
                {section.title}
              </Badge>
            </Head>
            <List>
              {section.features.map((feature) => (
                <li key={feature}>{FEATURE_LABELS[feature] || feature}</li>
              ))}
            </List>
          </Card>
        ))}
      </Grid>
    </Page>
  );
};

export default RolePermissionsPage;

const Page = styled.div`
  min-height: 100vh;
  padding: 16px;
  background: #f3f5fb;
  display: grid;
  gap: 12px;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  h1 { margin: 0; font-size: 1.4rem; }
  p { margin: 6px 0 0; color: #64748b; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 10px;
`;

const EditorCard = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
`;

const HeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  h3 {
    margin: 0;
    color: #0f172a;
  }
`;

const ActionRow = styled.div`
  display: inline-flex;
  gap: 8px;
`;

const ActionBtn = styled.button`
  border-radius: 10px;
  border: 1px solid ${({ $primary }) => ($primary ? "#015b9a" : "#cbd5e1")};
  background: ${({ $primary }) => ($primary ? "#015b9a" : "#fff")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#0f172a")};
  font-weight: 700;
  padding: 8px 12px;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Hint = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #166534;
`;

const TableWrap = styled.div`
  margin-top: 10px;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
`;

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 860px;
  th,
  td {
    border-bottom: 1px solid #e2e8f0;
    padding: 8px 10px;
    text-align: center;
    font-size: 12px;
  }
  th:first-child,
  td:first-child {
    text-align: left;
    font-weight: 700;
    color: #0f172a;
    min-width: 180px;
  }
  thead {
    background: #f8fafc;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
`;

const Head = styled.div`
  margin-bottom: 10px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 800;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #334155;
  display: grid;
  gap: 6px;
`;
